/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { 
  Play, 
  Square, 
  RotateCcw, 
  RefreshCw, 
  ExternalLink, 
  Terminal, 
  Clock,
  DollarSign,
  GitBranch,
  Server,
  Loader
} from 'lucide-react';
import type { Deployment } from '../../lib/api';
import { 
  getDeploymentStatusColor, 
  getDeploymentStatusLabel, 
  getEnvironmentTypeLabel,
  formatCost,
  formatUptime 
} from '../../lib/api/deployments';

interface DeploymentCardProps {
  deployment: Deployment;
  isOperating: boolean;
  operationInProgress?: string;
  onStart: () => void;
  onStop: () => void;
  onRestart: () => void;
  onUpdate: () => void;
  onViewLogs: () => void;
  onViewDetails: () => void;
}

export function DeploymentCard({
  deployment,
  isOperating,
  operationInProgress,
  onStart,
  onStop,
  onRestart,
  onUpdate,
  onViewLogs,
  onViewDetails,
}: DeploymentCardProps) {
  const statusColor = getDeploymentStatusColor(deployment.status);
  const statusLabel = getDeploymentStatusLabel(deployment.status);
  const environmentLabel = getEnvironmentTypeLabel(deployment.environmentType);
  
  const getStatusIndicatorColor = () => {
    switch (statusColor) {
      case 'green': return '#22c55e';
      case 'yellow': return '#eab308';
      case 'red': return '#ef4444';
      default: return '#9ca3af';
    }
  };

  const getStatusBackgroundColor = () => {
    switch (statusColor) {
      case 'green': return '#dcfce7';
      case 'yellow': return '#fef3c7';
      case 'red': return '#fee2e2';
      default: return '#f3f4f6';
    }
  };

  const isRunning = deployment.status === 'running';
  const isStopped = deployment.status === 'stopped';
  const canStart = isStopped || deployment.status === 'failed';
  const canStop = isRunning || deployment.status === 'unhealthy';
  const canRestart = isRunning || deployment.status === 'unhealthy';
  const canUpdate = isRunning || isStopped;

  const renderActionButton = (
    label: string,
    icon: React.ReactNode,
    onClick: () => void,
    disabled: boolean,
    variant: 'primary' | 'secondary' | 'danger' = 'secondary'
  ) => {
    const bgColors = {
      primary: '#0066cc',
      secondary: '#f3f4f6',
      danger: '#ef4444',
    };
    const textColors = {
      primary: '#ffffff',
      secondary: '#374151',
      danger: '#ffffff',
    };

    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        disabled={disabled || isOperating}
        className="btn"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 12px',
          fontSize: '12px',
          backgroundColor: disabled ? '#e5e7eb' : bgColors[variant],
          color: disabled ? '#9ca3af' : textColors[variant],
          border: variant === 'secondary' ? '1px solid #d1d5db' : 'none',
          borderRadius: '6px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s',
        }}
        title={label}
      >
        {isOperating && operationInProgress === label.toLowerCase() ? (
          <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
        ) : (
          icon
        )}
        {label}
      </button>
    );
  };

  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        padding: '20px',
        background: 'white',
        cursor: 'pointer',
        transition: 'all 0.2s',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      }}
      onClick={onViewDetails}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
        e.currentTarget.style.borderColor = '#d1d5db';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
        e.currentTarget.style.borderColor = '#e5e7eb';
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>
              {deployment.environmentName}
            </h3>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 10px',
                borderRadius: '9999px',
                fontSize: '12px',
                fontWeight: 500,
                backgroundColor: getStatusBackgroundColor(),
                color: getStatusIndicatorColor(),
              }}
            >
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: getStatusIndicatorColor(),
                  animation: statusColor === 'yellow' ? 'pulse 2s infinite' : undefined,
                }}
              />
              {statusLabel}
            </span>
          </div>
          <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
            {environmentLabel}
          </p>
        </div>
        
        {/* Quick Actions */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {canStart && renderActionButton('Start', <Play size={14} />, onStart, false, 'primary')}
          {canStop && renderActionButton('Stop', <Square size={14} />, onStop, false, 'secondary')}
          {canRestart && renderActionButton('Restart', <RotateCcw size={14} />, onRestart, false, 'secondary')}
          {canUpdate && renderActionButton('Update', <RefreshCw size={14} />, onUpdate, false, 'primary')}
        </div>
      </div>

      {/* URL */}
      {deployment.domain && (
        <a
          href={`https://${deployment.domain}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            color: '#0066cc',
            textDecoration: 'none',
            marginBottom: '16px',
          }}
        >
          <ExternalLink size={14} />
          {deployment.domain}
        </a>
      )}

      {/* Stats Row */}
      <div style={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: '24px', 
        padding: '12px 0', 
        borderTop: '1px solid #f3f4f6',
        fontSize: '13px',
        color: '#6b7280'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Clock size={14} />
          <span>Uptime: {formatUptime(deployment.uptime)}</span>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <DollarSign size={14} />
          <span>{formatCost(deployment.monthlyCostProjection)}/mo</span>
        </div>
        
        {deployment.gitBranch && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <GitBranch size={14} />
            <span>{deployment.gitBranch}</span>
          </div>
        )}
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Server size={14} />
          <span>{deployment.machineType}</span>
        </div>
      </div>

      {/* Footer Actions */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        paddingTop: '12px',
        borderTop: '1px solid #f3f4f6',
        marginTop: '4px'
      }}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onViewLogs();
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            fontSize: '13px',
            color: '#6b7280',
            background: 'none',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        >
          <Terminal size={14} />
          View Logs
        </button>
        
        {deployment.deployedAt && (
          <span style={{ fontSize: '12px', color: '#9ca3af' }}>
            Last deployed: {new Date(deployment.deployedAt).toLocaleDateString()}
          </span>
        )}
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

